import { InventoryListing } from "services/inventory/types";

import {
  formatAssetDetails,
  formatDollarAge,
  formatHighestOpenOffer,
  formatListPriceOrSalePrice,
  formatLTM,
  formatMarketplaceMedian,
  formatMedia,
} from "./columnFormat";
import { TTableColumn } from "./types";

export const desktopOpenListingsAuthenticated: TTableColumn<InventoryListing>[] = [
  {
    primaryKey: "media_url",
    label: "Media",
    sortable: false,
    width: "1px",
    format: formatMedia,
    preventSelect: true,
  },
  {
    primaryKey: "term",
    secondaryKey: "term_expiration",
    label: "Asset",
    helpText: "Title, asset term, and ID number for each listing.",
    sortable: true,
    width: "44%",
    format: formatAssetDetails,
  },
  {
    primaryKey: "ltm",
    label: "Last 12 Mo.<br />Earnings",
    helpText:
      "The total royalty earnings of this catalog from the last 12 months.",
    sortable: true,
    width: "12%",
    format: formatLTM,
  },
  {
    primaryKey: "dollar_age",
    label: "Dollar<br />Age",
    helpText: "A calculated metric used to show the age of a catalog.",
    sortable: true,
    width: "7%",
    format: formatDollarAge,
  },
  {
    primaryKey: "list_price",
    secondaryKey: "list_price_multiple",
    label: "List Price",
    helpText:
      "The price that the asset owner is willing to accept for an immediate sale.",
    sortable: true,
    width: "10%",
    format: formatListPriceOrSalePrice,
  },
  {
    primaryKey: "marketplace_median",
    label: "Marketplace<br />Median",
    helpText: "Median pricing for your offers and purchases.",
    canBreakLabel: true,
    sortable: true,
    width: "17%",
    format: formatMarketplaceMedian(),
  },
  {
    primaryKey: "highest_open_offer_amount",
    label: "Highest Open&nbsp;Offer",
    canBreakLabel: true,
    helpText: "The highest open offer for each listing.",
    sortable: true,
    width: "10%",
    format: formatHighestOpenOffer,
  },
];

export const desktopOpenListingsAnonymous: TTableColumn<InventoryListing>[] = [
  {
    primaryKey: "media_url",
    label: "Media",
    sortable: false,
    width: "1px",
    format: formatMedia,
    preventSelect: true,
  },
  {
    primaryKey: "term",
    secondaryKey: "term_expiration",
    label: "Asset",
    helpText: "Title, asset term, and ID number for each listing.",
    sortable: true,
    width: "44%",
    format: formatAssetDetails,
  },
  {
    primaryKey: "ltm",
    label: "Last 12 Mo.<br />Earnings",
    helpText:
      "The total royalty earnings of this catalog from the last 12 months.",
    sortable: true,
    width: "12%",
    format: formatLTM,
  },
  {
    primaryKey: "dollar_age",
    label: "Dollar<br />Age",
    helpText: "A calculated metric used to show the age of a catalog.",
    sortable: true,
    width: "7%",
    format: formatDollarAge,
  },
  {
    primaryKey: "list_price",
    secondaryKey: "list_price_multiple",
    label: "List Price",
    helpText:
      "The price that the asset owner is willing to accept for an immediate sale.",
    sortable: true,
    width: "10%",
    format: formatListPriceOrSalePrice,
  },
  {
    primaryKey: "marketplace_median",
    label: "Marketplace<br />Median",
    helpText:
      "Royalty Exchange users get access to Marketplace Median pricing for " +
      "all listings.",
    canBreakLabel: true,
    sortable: true,
    width: "17%",
    format: formatMarketplaceMedian(),
    preventSelect: true,
  },
  {
    primaryKey: "highest_open_offer_amount",
    label: "Highest Open&nbsp;Offer",
    canBreakLabel: true,
    helpText: "The highest open offer for each listing.",
    sortable: true,
    width: "10%",
    format: formatHighestOpenOffer,
  },
];

export const mobileOpenListings: TTableColumn<InventoryListing>[] = [
  {
    label: "Asset",
    helpText: "Title, asset term, and ID number for each listing.",
    primaryKey: "term",
    format: formatAssetDetails,
    width: "70%",
    minWidth: "62px",
  },
  {
    primaryKey: "ltm",
    label: "Last 12 Mo. Earnings",
    helpText:
      "The total royalty earnings of this catalog from the last 12 months.",
    canBreakLabel: true,
    sortable: true,
    format: formatLTM,
    width: "30%",
  },
];

export const desktopPastDeals: TTableColumn<InventoryListing>[] = [
  {
    primaryKey: "media_url",
    label: "Media",
    sortable: false,
    width: "1px",
    format: formatMedia,
    preventSelect: true,
  },
  {
    primaryKey: "term",
    secondaryKey: "term_expiration",
    label: "Asset",
    helpText: "Title, asset term, and ID number for each listing.",
    sortable: true,
    width: "44%",
    format: formatAssetDetails,
  },
  {
    primaryKey: "deal_date",
    label: "Sale Price",
    helpText: "The price that the asset was sold.",
    sortable: false,
    width: "15%",
    format: formatListPriceOrSalePrice,
  },
  {
    primaryKey: "ltm",
    label: "Last 12 Mo.<br />Earnings",
    helpText:
      "The total royalty earnings of this catalog from the last 12 months.",
    sortable: true,
    width: "12%",
    format: formatLTM,
  },
  {
    primaryKey: "dollar_age",
    label: "Dollar<br />Age",
    helpText: "A calculated metric used to show the age of a catalog.",
    sortable: true,
    width: "7%",
    format: formatDollarAge,
  },
  {
    primaryKey: "marketplace_median",
    label: "Marketplace<br />Median",
    helpText: "Median pricing for your offers and purchases.",
    sortable: true,
    width: "17%",
    format: formatMarketplaceMedian(),
  },
];

export const desktopPastDealsAnonymous: TTableColumn<InventoryListing>[] = [
  {
    primaryKey: "media_url",
    label: "Media",
    sortable: false,
    width: "1px",
    format: formatMedia,
    preventSelect: true,
  },
  {
    primaryKey: "term",
    secondaryKey: "term_expiration",
    label: "Asset",
    helpText: "Title, asset term, and ID number for each listing.",
    sortable: true,
    width: "44%",
    format: formatAssetDetails,
  },
  {
    primaryKey: "deal_date",
    label: "Sale Price",
    helpText: "The price that the asset was sold.",
    sortable: false,
    width: "15%",
    format: formatListPriceOrSalePrice,
  },
  {
    primaryKey: "ltm",
    label: "Last 12 Mo.<br />Earnings",
    helpText:
      "The total royalty earnings of this catalog from the last 12 months.",
    sortable: true,
    width: "12%",
    format: formatLTM,
  },
  {
    primaryKey: "dollar_age",
    label: "Dollar<br />Age",
    helpText: "A calculated metric used to show the age of a catalog.",
    sortable: true,
    width: "7%",
    format: formatDollarAge,
  },
  {
    primaryKey: "marketplace_median",
    label: "Marketplace<br />Median",
    helpText:
      "Royalty Exchange users get access to Marketplace Median pricing for " +
      "all listings.",
    canBreakLabel: true,
    sortable: true,
    width: "17%",
    format: formatMarketplaceMedian(),
    preventSelect: true,
  },
];

export const mobilePastDeals: TTableColumn<InventoryListing>[] = [
  {
    label: "Asset",
    helpText: "Title, asset term, and ID number for each listing.",
    primaryKey: "term",
    format: formatAssetDetails,
    width: "70%",
    minWidth: "62px",
  },
  {
    primaryKey: "deal_date",
    label: "Sale Price",
    helpText: "The price that the asset was sold.",
    sortable: false,
    width: "15%",
    format: formatListPriceOrSalePrice,
  },
];

export const desktopMyListings: TTableColumn<InventoryListing>[] = [
  {
    primaryKey: "media_url",
    label: "Media",
    sortable: false,
    width: "1px",
    format: formatMedia,
    preventSelect: true,
  },
  {
    primaryKey: "term",
    secondaryKey: "term_expiration",
    label: "Asset",
    helpText: "Title, asset term, and ID number for each listing.",
    sortable: true,
    width: "44%",
    format: formatAssetDetails,
  },
  {
    primaryKey: "ltm",
    label: "Last 12 Mo.<br />Earnings",
    helpText:
      "The total royalty earnings of this catalog from the last 12 months.",
    sortable: true,
    width: "12%",
    format: formatLTM,
  },
  {
    primaryKey: "dollar_age",
    label: "Dollar<br />Age",
    helpText: "A calculated metric used to show the age of a catalog.",
    sortable: true,
    width: "7%",
    format: formatDollarAge,
  },
  {
    primaryKey: "list_price",
    secondaryKey: "list_price_multiple",
    label: "List Price/<br />Sale Price",
    helpText:
      "The price that the asset owner is willing to accept for an immediate sale.",
    sortable: true,
    width: "10%",
    format: formatListPriceOrSalePrice,
  },
  {
    primaryKey: "marketplace_median",
    label: "Marketplace<br />Median",
    helpText: "Median pricing for your offers and purchases.",
    canBreakLabel: true,
    sortable: true,
    width: "17%",
    format: formatMarketplaceMedian(),
  },
  {
    primaryKey: "highest_open_offer_amount",
    label: "Highest Open&nbsp;Offer",
    canBreakLabel: true,
    helpText: "The highest open offer for each listing.",
    sortable: true,
    width: "10%",
    format: formatHighestOpenOffer,
  },
];

export const mobileMyListings: TTableColumn<InventoryListing>[] = [
  {
    label: "Asset",
    helpText: "Title, asset term, and ID number for each listing.",
    primaryKey: "term",
    format: formatAssetDetails,
    width: "70%",
    minWidth: "62px",
  },
  {
    primaryKey: "ltm",
    label: "Last 12 Mo. Earnings",
    helpText:
      "The total royalty earnings of this catalog from the last 12 months.",
    canBreakLabel: true,
    sortable: true,
    format: formatLTM,
    width: "30%",
  },
];
